import { useState } from 'react';
import { Button } from './ui/Button';
import { Markdown } from './ui/Markdown';
import type { TicketDraft, UpdateTicketDraftInput } from '@pantheon/shared';

export interface TicketCardProps {
  ticket: TicketDraft;
  onUpdate: (draftId: string, input: UpdateTicketDraftInput) => void;
  readOnly?: boolean;
  className?: string;
}

const typeColors: Record<string, string> = {
  epic: 'bg-purple-100 text-purple-700',
  story: 'bg-green-100 text-green-700',
  task: 'bg-blue-100 text-blue-700',
  subtask: 'bg-cyan-100 text-cyan-700',
  bug: 'bg-red-100 text-red-700',
};

const typeBorders: Record<string, string> = {
  epic: 'border-l-purple-500',
  story: 'border-l-green-500',
  task: 'border-l-blue-500',
  subtask: 'border-l-cyan-500',
  bug: 'border-l-red-500',
};

const priorityLabels: Record<string, string> = {
  highest: '\u2191\u2191 Highest',
  high: '\u2191 High',
  medium: '- Medium',
  low: '\u2193 Low',
  lowest: '\u2193\u2193 Lowest',
};

export function TicketCard({ ticket, onUpdate, readOnly, className = '' }: TicketCardProps) {
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState(ticket.title);
  const [description, setDescription] = useState(ticket.description);

  const handleSave = () => {
    onUpdate(ticket.draftId, { title, description });
    setEditing(false);
  };

  const handleCancel = () => {
    setTitle(ticket.title);
    setDescription(ticket.description);
    setEditing(false);
  };

  return (
    <div className={`bg-white border border-slate-100 border-l-4 ${typeBorders[ticket.type] ?? 'border-l-gray-400'} rounded-xl shadow-sm shadow-slate-200/50 p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase ${typeColors[ticket.type] ?? 'bg-gray-100 text-gray-600'}`}>
            {ticket.type}
          </span>
          <span className="text-xs text-gray-500">{priorityLabels[ticket.priority]}</span>
          <span className="text-xs text-gray-400">#{ticket.order}</span>
          {ticket.type !== 'epic' && (
            <select
              className={`text-xs px-1.5 py-0.5 rounded border font-medium ${readOnly ? 'cursor-default opacity-75' : 'cursor-pointer'} ${
                (ticket.routing ?? 'daedalus') === 'daedalus'
                  ? 'bg-blue-50 text-blue-600 border-blue-200'
                  : 'bg-amber-50 text-amber-600 border-amber-200'
              }`}
              value={ticket.routing ?? 'daedalus'}
              onChange={(e) => onUpdate(ticket.draftId, { routing: e.target.value as 'daedalus' | 'developer' })}
              disabled={readOnly}
            >
              <option value="daedalus">Daedalus</option>
              <option value="developer">Developer</option>
            </select>
          )}
        </div>
        <div className="flex items-center gap-2">
          {ticket.metadata.aiConfidence < 0.7 && (
            <span className="text-xs text-warning font-medium">Low confidence</span>
          )}
          {ticket.userEdited && (
            <span className="text-xs text-primary font-medium">Edited</span>
          )}
          {!readOnly && (
            <Button variant="ghost" size="sm" onClick={() => setEditing(!editing)}>
              {editing ? 'Cancel' : 'Edit'}
            </Button>
          )}
        </div>
      </div>

      {/* Title */}
      {editing ? (
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full text-sm font-semibold mb-2 px-2 py-1 border border-gray-300 rounded"
        />
      ) : (
        <h4 className="text-sm font-semibold text-gray-900 mb-2">{ticket.title}</h4>
      )}

      {/* Description + expandable details */}
      {editing ? (
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          className="w-full text-sm mb-2 px-2 py-1 border border-gray-300 rounded resize-y"
        />
      ) : (
        <>
          {expanded ? (
            <Markdown className="mb-2">{ticket.description}</Markdown>
          ) : (
            // Collapsed preview still renders markdown (not raw), clipped to a
            // couple of lines — markdown block elements don't line-clamp cleanly,
            // so bound the height and hide the overflow instead.
            <div className="mb-2 max-h-12 overflow-hidden">
              <Markdown>{ticket.description}</Markdown>
            </div>
          )}
          {(ticket.acceptanceCriteria.length > 0 || (ticket.description?.length ?? 0) > 140) && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-xs text-primary hover:underline mb-3 inline-flex items-center gap-1"
            >
              <svg className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} viewBox="0 0 20 20" fill="currentColor"><path d="M7 5l6 5-6 5V5z" /></svg>
              {expanded
                ? 'Hide details'
                : ticket.acceptanceCriteria.length > 0
                  ? `Details · ${ticket.acceptanceCriteria.length} acceptance criteria`
                  : 'Show full description'}
            </button>
          )}
          {expanded && ticket.acceptanceCriteria.length > 0 && (
            <div className="text-xs text-gray-500 mb-3">
              <p className="font-semibold mb-1">Acceptance Criteria:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                {ticket.acceptanceCriteria.map((ac, i) => <li key={i}>{ac}</li>)}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Tags */}
      <div className="flex flex-wrap items-center gap-1.5">
        {ticket.labels.map((label) => (
          <span key={label} className="bg-gray-100 text-gray-500 px-2.5 py-0.5 rounded-full text-xs">{label}</span>
        ))}
        {ticket.estimatePoints != null && (
          <span className="text-xs text-gray-500">{ticket.estimatePoints} pts</span>
        )}
        {ticket.metadata.suggestedBranch && (
          <span className="text-xs text-gray-500 font-mono">{ticket.metadata.suggestedBranch}</span>
        )}
      </div>

      {/* Edit actions */}
      {editing && (
        <div className="flex gap-2 mt-3">
          <Button size="sm" onClick={handleSave}>Save</Button>
          <Button size="sm" variant="secondary" onClick={handleCancel}>Cancel</Button>
        </div>
      )}
    </div>
  );
}
